import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchProjects, fetchPrototypes } from '../api';
import { qk } from '../api/queryKeys';

export interface WorkspaceProjectOption {
  id: string;
  name: string;
}

/**
 * The active workspace's projects (repos) + prototypes, combined into one
 * flat, name-sorted list — a grant's `projectId` targets either kind, so both
 * the per-member editor and the invite-time picker need the union. Reuses the
 * same list queries (and cache) the Projects/Prototypes pages already use.
 */
export function useWorkspaceProjectOptions(): {
  options: WorkspaceProjectOption[];
  isLoading: boolean;
} {
  const projectsQuery = useQuery({ queryKey: qk.projects, queryFn: fetchProjects });
  const prototypesQuery = useQuery({ queryKey: qk.prototypes, queryFn: fetchPrototypes });

  const options = useMemo(() => {
    const projects = projectsQuery.data ?? [];
    const prototypes = prototypesQuery.data ?? [];
    return [...projects, ...prototypes]
      .map((p) => ({ id: p.id, name: p.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projectsQuery.data, prototypesQuery.data]);

  return {
    options,
    isLoading: projectsQuery.isLoading || prototypesQuery.isLoading,
  };
}
